import { type ReactNode, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { Alert } from '@gto/ui/alert'
import { Autocomplete } from '@gto/ui/autocomplete'
import { CircularProgress } from '@gto/ui/circular-progress'
import { Field } from '@gto/ui/field'
import { ToggleButton, ToggleButtonGroup } from '@gto/ui/toggle-button'
import { Typography } from '@gto/ui/typography'

import { SelectField } from '@/components/form'
import { PlanName } from '@/components/plan-name'
import {
  type AffiliateCouponTagSchema,
  type AffiliateCreateCouponTagRequest,
  buildPlanScope,
  type CouponPlan,
  type CouponSharedFormValues,
  DEFAULT_COUPON_DISCOUNT_PERCENT,
  ELIGIBLE_INTERVALS,
  ELIGIBLE_TIERS,
  eligibleProducts,
  isEffectivelyZeroDiscount,
  planNameString,
  type PlanScopeKind,
  productKey,
  resolveScopedPlans,
  selectablePlans,
} from '@/domain/coupons'

import { ControlledNumberField } from './controlled-number-field'
import { couponErrorText } from './coupon-field-error'
import { CouponSwitchRow, type CouponSwitchRowProps } from './coupon-switch-row'
import { CouponTagsField } from './coupon-tags-field'

type PlansState = {
  plans: CouponPlan[]
  plansLoading: boolean
  plansError: boolean
}

export type CouponTagsState = {
  tags: AffiliateCouponTagSchema[]
  tagsLoading: boolean
  tagsError: boolean
  creatingTag: boolean
  createTagError: boolean
  createTagServerError: unknown
  deletingTag: boolean
  deleteTagError: boolean
  onCreateTag: (
    request: AffiliateCreateCouponTagRequest,
  ) => Promise<AffiliateCouponTagSchema | null>
  onDeleteTag: (tagId: string) => Promise<boolean>
}

type ControlledSwitchRowProps = Omit<CouponSwitchRowProps, 'checked' | 'onCheckedChange'> & {
  name: 'isOneUsePerUser' | 'firstPaymentOnly'
}

const ControlledSwitchRow = ({ name, label, description }: ControlledSwitchRowProps) => {
  const { control } = useFormContext<CouponSharedFormValues>()

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <CouponSwitchRow
          checked={field.value}
          description={description}
          label={label}
          onCheckedChange={field.onChange}
        />
      )}
    />
  )
}

const IntervalSelect = () => {
  const { formatMessage } = useIntl()
  const { control } = useFormContext<CouponSharedFormValues>()

  return (
    <Controller
      control={control}
      name="scopeInterval"
      render={({ field, fieldState }) => (
        <SelectField
          className="flex-1"
          error={couponErrorText(formatMessage, fieldState.error)}
          label={formatMessage({ id: 'coupon.scope.intervalLabel' })}
          name={field.name}
          triggerRef={field.ref}
          value={field.value}
          options={ELIGIBLE_INTERVALS.map((interval) => ({
            value: interval,
            label: formatMessage({ id: `plan.billing.${interval}`, defaultMessage: interval }),
          }))}
          onBlur={field.onBlur}
          onValueChange={field.onChange}
        />
      )}
    />
  )
}

const isScopeKind = (value: unknown): value is Exclude<PlanScopeKind, 'custom'> =>
  value === 'all' || value === 'specific' || value === 'tier' || value === 'product'

/**
 * The searchable single-plan picker. Owns its own open/input state and writes
 * the chosen plan id to the `scopePlanId` form field via context, so the parent
 * scope selector only has to switch on `scopeKind`.
 */
const SpecificPlanAutocomplete = ({ plans }: { plans: CouponPlan[] }) => {
  const { formatMessage } = useIntl()
  const { control } = useFormContext<CouponSharedFormValues>()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')

  const options = selectablePlans(plans).map((plan) => ({
    label: [
      plan.variant === null
        ? null
        : formatMessage({ id: `plan.game.${plan.variant}`, defaultMessage: plan.variant }),
      formatMessage({ id: `plan.format.${plan.type}`, defaultMessage: plan.type }),
      formatMessage({ id: `plan.tier.${plan.tier}`, defaultMessage: plan.tier }),
      formatMessage({ id: `plan.billing.${plan.interval}`, defaultMessage: plan.interval }),
    ]
      .filter((part) => part !== null)
      .join(' · '),
    value: String(plan.id),
  }))

  return (
    <Controller
      control={control}
      name="scopePlanId"
      render={({ field, fieldState }) => {
        const selectedLabel = options.find((option) => option.value === field.value)?.label ?? ''
        const query = open ? input : selectedLabel
        const normalizedQuery = query.trim().toLocaleLowerCase()
        const filtered = options.filter((option) =>
          option.label.toLocaleLowerCase().includes(normalizedQuery),
        )

        return (
          <Field.Root invalid={fieldState.error !== undefined}>
            <Field.Label htmlFor="coupon-scope-plan">
              <FormattedMessage id="coupon.scope.planLabel" />
            </Field.Label>
            <Autocomplete.Root
              autoHighlight
              items={filtered}
              open={open}
              value={query}
              onOpenChange={setOpen}
              onValueChange={(value, details) => {
                setInput(value)
                if (details.reason === 'input-change' || details.reason === 'input-clear') {
                  field.onChange('')
                }
              }}
            >
              <Autocomplete.Input
                aria-invalid={fieldState.error !== undefined}
                data-tst="coupon-scope-plan"
                id="coupon-scope-plan"
                onFocus={() => {
                  setInput('')
                  setOpen(true)
                }}
              />
              <Autocomplete.Portal>
                <Autocomplete.Positioner>
                  <Autocomplete.Popup>
                    <Autocomplete.List>
                      {filtered.map((option) => (
                        <Autocomplete.Item
                          key={option.value}
                          value={option}
                          onClick={() => field.onChange(option.value)}
                        >
                          {option.label}
                        </Autocomplete.Item>
                      ))}
                      <Autocomplete.Empty className="p-2 text-center text-sm text-text-disabled">
                        <FormattedMessage id="coupon.scope.noPlanMatches" />
                      </Autocomplete.Empty>
                    </Autocomplete.List>
                  </Autocomplete.Popup>
                </Autocomplete.Positioner>
              </Autocomplete.Portal>
            </Autocomplete.Root>
            <Field.Error match>{couponErrorText(formatMessage, fieldState.error)}</Field.Error>
          </Field.Root>
        )
      }}
    />
  )
}

/** The 4-option plan-scope selector + live resolved-plans preview. */
const PlanScopeField = ({ plans, plansLoading, plansError }: PlansState) => {
  const { formatMessage } = useIntl()
  const { control, setValue } = useFormContext<CouponSharedFormValues>()
  const [scopeKind, scopePlanId, scopeTier, scopeProduct, scopeInterval, customPlanIds] = useWatch({
    control,
    name: [
      'scopeKind',
      'scopePlanId',
      'scopeTier',
      'scopeProduct',
      'scopeInterval',
      'customPlanIds',
    ],
  })

  const products = eligibleProducts(plans)
  const scopedDisabled = plansError || plansLoading

  const scope = buildPlanScope({
    scopeKind,
    scopePlanId,
    scopeTier,
    scopeProduct,
    scopeInterval,
    customPlanIds,
  })
  const preview = scope === null || scope.kind === 'all' ? [] : resolveScopedPlans(scope, plans)

  const tierLabel = (tier: string) =>
    formatMessage({ id: `plan.tier.${tier}`, defaultMessage: tier })
  const productLabel = (product: { variant: string | null; type: string }) => {
    const game =
      product.variant === null
        ? ''
        : formatMessage({ id: `plan.game.${product.variant}`, defaultMessage: product.variant })
    const format = formatMessage({
      id: `plan.format.${product.type}`,
      defaultMessage: product.type,
    })

    return game === '' ? format : `${game} · ${format}`
  }

  return (
    <div className="flex flex-col gap-2">
      <Typography
        color="text.secondary"
        variant="caption"
      >
        {formatMessage({ id: 'coupon.scope.label' })}
      </Typography>
      <ToggleButtonGroup
        aria-label={formatMessage({ id: 'coupon.scope.label' })}
        size="small"
        value={scopeKind === 'custom' ? '' : scopeKind}
        onValueChange={(value) => {
          if (isScopeKind(value)) {
            setValue('scopeKind', value, { shouldValidate: true })
          }
        }}
      >
        <ToggleButton value="all">{formatMessage({ id: 'coupon.scope.all' })}</ToggleButton>
        <ToggleButton
          disabled={scopedDisabled}
          value="specific"
        >
          {formatMessage({ id: 'coupon.scope.specific' })}
        </ToggleButton>
        <ToggleButton
          disabled={scopedDisabled}
          value="tier"
        >
          {formatMessage({ id: 'coupon.scope.tier' })}
        </ToggleButton>
        <ToggleButton
          disabled={scopedDisabled}
          value="product"
        >
          {formatMessage({ id: 'coupon.scope.product' })}
        </ToggleButton>
      </ToggleButtonGroup>

      {plansLoading && (
        <div
          className="flex flex-row items-center gap-2"
          role="status"
        >
          <CircularProgress
            aria-hidden
            size={16}
          />
          <Typography
            color="text.secondary"
            variant="caption"
          >
            {formatMessage({ id: 'coupon.scope.loading' })}
          </Typography>
        </div>
      )}

      {plansError && (
        <Alert severity="warning">{formatMessage({ id: 'coupon.scope.loadError' })}</Alert>
      )}

      {scopeKind === 'custom' && (
        <Typography
          color="text.secondary"
          variant="caption"
        >
          {formatMessage({ id: 'coupon.scope.customNote' }, { count: customPlanIds.length })}
        </Typography>
      )}

      {scopeKind === 'specific' && <SpecificPlanAutocomplete plans={plans} />}

      {scopeKind === 'tier' && (
        <div className="flex flex-row gap-3">
          <Controller
            control={control}
            name="scopeTier"
            render={({ field, fieldState }) => (
              <SelectField
                className="flex-1"
                error={couponErrorText(formatMessage, fieldState.error)}
                label={formatMessage({ id: 'coupon.scope.tierLabel' })}
                name={field.name}
                triggerRef={field.ref}
                value={field.value}
                options={ELIGIBLE_TIERS.map((tier) => ({
                  value: tier,
                  label: tierLabel(tier),
                }))}
                onBlur={field.onBlur}
                onValueChange={field.onChange}
              />
            )}
          />
          <IntervalSelect />
        </div>
      )}

      {scopeKind === 'product' && (
        <div className="flex flex-row gap-3">
          <Controller
            control={control}
            name="scopeProduct"
            render={({ field, fieldState }) => (
              <SelectField
                className="flex-1"
                error={couponErrorText(formatMessage, fieldState.error)}
                label={formatMessage({ id: 'coupon.scope.productLabel' })}
                name={field.name}
                triggerRef={field.ref}
                value={field.value}
                options={products.map((product) => ({
                  value: productKey(product),
                  label: productLabel(product),
                }))}
                onBlur={field.onBlur}
                onValueChange={field.onChange}
              />
            )}
          />
          <IntervalSelect />
        </div>
      )}

      {scope !== null && scope.kind !== 'all' && (
        <div className="flex flex-col gap-1.5">
          {/* `custom` already states its authoritative count in `customNote`
              above (from customPlanIds); the resolved-chip count can be lower
              when a legacy id is absent from the catalog, so don't show a second,
              conflicting count for it — just the chips it could resolve. */}
          {scope.kind !== 'custom' && (
            <Typography
              color="text.secondary"
              variant="caption"
            >
              {preview.length === 0
                ? formatMessage({ id: 'coupon.scope.previewEmpty' })
                : formatMessage({ id: 'coupon.scope.previewCount' }, { count: preview.length })}
            </Typography>
          )}
          {preview.length > 0 && (
            <div
              className="flex flex-row flex-wrap gap-1.5"
              data-tst="coupon-scope-preview"
            >
              {preview.map((plan) => (
                <PlanName
                  key={plan.id}
                  value={planNameString(plan)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** The discount input plus its 0% warning. A 0% link gives the buyer no sign-up
 *  discount, which is almost always a slip rather than an intent (MKT-441) — so
 *  warn, but never block: a deliberate 0% link stays submittable. The watch that
 *  drives the warning lives here so it re-renders this field alone, not every
 *  shared field.
 *
 *  The warning is a `Field.Description` (rendered AS the DS `Alert`) rather than
 *  a sibling node: Base UI registers each message's id and composes the control's
 *  `aria-describedby` from them, so the warning is associated with the input
 *  alongside its helper text without this component naming a single id. That
 *  association is what surfaces the warning at all when the edit dialog OPENS on
 *  a stored 0% link — `role="alert"` only announces an insertion, never content
 *  already on screen at mount. */
const DiscountField = ({ namesDefault }: { namesDefault: boolean }) => {
  const { formatMessage } = useIntl()
  const { control } = useFormContext<CouponSharedFormValues>()
  const discountValue = useWatch({ control, name: 'discountValue' })

  const showZeroWarning = isEffectivelyZeroDiscount(discountValue)

  return (
    <ControlledNumberField
      control={control}
      data-tst="coupon-discount"
      helperText={formatMessage({ id: 'admin.partnerDetail.coupons.discountHelper' })}
      label={formatMessage({ id: 'admin.partnerDetail.coupons.discount' })}
      max={100}
      min={0}
      name="discountValue"
      step={0.01}
      messageSlot={
        showZeroWarning && (
          <Field.Description
            render={
              <Alert
                data-tst="coupon-discount-zero-warning"
                severity="warning"
              />
            }
          >
            <FormattedMessage id="admin.partnerDetail.coupons.discount.zeroWarning" />
            {namesDefault && (
              <>
                {' '}
                <FormattedMessage
                  id="admin.partnerDetail.coupons.discount.zeroWarningDefault"
                  values={{ percent: DEFAULT_COUPON_DISCOUNT_PERCENT }}
                />
              </>
            )}
          </Field.Description>
        )
      }
    />
  )
}

type CouponSharedFieldsProps = PlansState & {
  tagState: CouponTagsState
  /** The single/edit-only "Max uses" field, rendered directly under the plan
   *  scope. Batch has no max-uses input (it is fixed at 1 server side), so it
   *  passes nothing. Owned by the parent form so it stays bound to that form's
   *  typed `maxUses` field, which the shared `CouponSharedFormValues` view omits. */
  maxUsesSlot?: ReactNode
  /** Whether to render the one-use-per-user toggle (default true). Batch fixes
   *  `max_uses` at 1 server side, so each generated link is single-use anyway and
   *  the per-user flag can never take effect — batch passes false to hide the
   *  otherwise-inert control. The field stays in the shared schema, so batch
   *  still sends its default (`true`) on the wire, unchanged. */
  showOneUsePerUser?: boolean
  /** Whether the 0% warning also names the pre-filled default (default false).
   *  Only single-create pre-fills one; batch starts blank and edit seeds from the
   *  stored link, so neither may claim it. Every form still WARNS — a 0% batch is
   *  the costliest version of the mistake (up to 500 links) — it just doesn't
   *  advertise a default it doesn't apply. */
  namesDefaultDiscount?: boolean
}

/** Discount + plan-scope + optional tags/max-uses + expiry + the redemption toggles
 *  — the fields every coupon form (single, batch, edit) shares. Reads the form
 *  via context; the one-use-per-user toggle sits last. */
export const CouponSharedFields = ({
  plans,
  plansLoading,
  plansError,
  tagState,
  maxUsesSlot,
  showOneUsePerUser = true,
  namesDefaultDiscount = false,
}: CouponSharedFieldsProps) => {
  const { formatMessage } = useIntl()
  const { control } = useFormContext<CouponSharedFormValues>()

  return (
    <>
      <DiscountField namesDefault={namesDefaultDiscount} />
      <PlanScopeField
        plans={plans}
        plansError={plansError}
        plansLoading={plansLoading}
      />
      <Controller
        control={control}
        name="tagIds"
        render={({ field }) => (
          <CouponTagsField
            createError={tagState.createTagError}
            createServerError={tagState.createTagServerError}
            creating={tagState.creatingTag}
            deleteError={tagState.deleteTagError}
            deleting={tagState.deletingTag}
            loadError={tagState.tagsError}
            loading={tagState.tagsLoading}
            tags={tagState.tags}
            value={field.value}
            onChange={field.onChange}
            onCreateTag={tagState.onCreateTag}
            onDeleteTag={tagState.onDeleteTag}
          />
        )}
      />
      {maxUsesSlot}
      <ControlledNumberField
        control={control}
        helperText={formatMessage({ id: 'coupon.daysValidHelper' })}
        label={formatMessage({ id: 'coupon.daysValid' })}
        min={1}
        name="daysValid"
      />
      <ControlledSwitchRow
        description={formatMessage({ id: 'coupon.firstPaymentOnlyHelper' })}
        label={formatMessage({ id: 'coupon.firstPaymentOnly' })}
        name="firstPaymentOnly"
      />
      {showOneUsePerUser && (
        <ControlledSwitchRow
          description={formatMessage({ id: 'coupon.oneUsePerUserHelper' })}
          label={formatMessage({ id: 'coupon.oneUsePerUser' })}
          name="isOneUsePerUser"
        />
      )}
    </>
  )
}
